import { useEffect, useRef } from 'react';

import {
  type SpeechActivityUpdate,
  useBrowserSpeechRecognition,
} from './narration/useBrowserSpeechRecognition';

type UseVideoToolboxSpeechListeningOptions = {
  open: boolean;
  enabled?: boolean;
  /** User opted in via Talk to text — recognition does not start on panel open. */
  listeningActive?: boolean;
  paused?: boolean;
  onSpeechActivityChange: (update: SpeechActivityUpdate) => void;
  onSpeechUnsupported: () => void;
};

/** Browser speech recognition for the toolbox composer; starts only when the user opts in. */
export const useVideoToolboxSpeechListening = ({
  open,
  enabled = true,
  listeningActive = false,
  paused = false,
  onSpeechActivityChange,
  onSpeechUnsupported,
}: UseVideoToolboxSpeechListeningOptions) => {
  const isListeningRef = useRef(false);
  const onSpeechUnsupportedRef = useRef(onSpeechUnsupported);
  onSpeechUnsupportedRef.current = onSpeechUnsupported;

  const {
    startRecognition,
    stopRecognition,
    isSupported,
    speechText,
    error,
  } = useBrowserSpeechRecognition({
    isRecordingRef: isListeningRef,
    onSpeechActivityChange,
  });

  const startRecognitionRef = useRef(startRecognition);
  const stopRecognitionRef = useRef(stopRecognition);
  startRecognitionRef.current = startRecognition;
  stopRecognitionRef.current = stopRecognition;

  useEffect(() => {
    const stopListening = () => {
      isListeningRef.current = false;
      stopRecognitionRef.current();
    };

    if (!open || !enabled || !listeningActive) {
      return stopListening;
    }

    if (!isSupported) {
      onSpeechUnsupportedRef.current();
      return stopListening;
    }

    if (paused) {
      return stopListening;
    }

    isListeningRef.current = true;
    startRecognitionRef.current();

    return stopListening;
  }, [enabled, isSupported, listeningActive, open, paused]);

  return {
    isListening: open && enabled && listeningActive && !paused && isSupported,
    isSupported,
    speechText,
    error,
  };
};
